/**
 * Instagram Story templates (1080×1920) — line-up posters with multiple shows.
 * Derived from Figma frames "ig-guest-story" (2039:38, navy, up to 3 shows) and
 * "Residency IG STORY" (2039:40, grey, up to 2 shows).
 *
 * Text uses "Space Mono". Per the brief, stories reuse the brand diamond
 * halftone back plate (navy for guest, grey for resident), not the Figma
 * frames' own layered pattern. The outer border, date/header/name/time panels
 * and photo slots are positioned natively from the Figma coordinates.
 */
import { COLORS } from './templates';
import { type Rect } from './photoSlot';

export const STORY_FONT = 'Space Mono';
export type StoryVariant = 'guest' | 'resident';

/** A navy/grey panel + orange border holding one line of Space Mono text. */
export interface StoryTextBox {
  box: Rect;
  fontSize: number;
  weight: number;
  align: 'left' | 'center' | 'right';
}

export interface StorySlot {
  photoFrame: Rect;
  /** Artist name (guest) or show name (resident). */
  name: StoryTextBox;
  /** Resident only: the performing artist, below the show name. */
  artist?: StoryTextBox;
  time: StoryTextBox;
  /** Small decorative connector lines (resident). */
  connectors?: { x: number; y: number; length: number }[];
}

export interface StoryTemplate {
  id: string;
  variant: StoryVariant;
  label: string;
  audience: 'artist' | 'internal';
  canvas: { width: number; height: number };
  /** Panel + logo-box fill colour (navy for guest, grey for resident). */
  fill: string;
  stroke: string;
  textColor: string;
  assets: { back: string; logo: string; logoRect: Rect };
  /** Big outer frame (Figma stroke align INSIDE). */
  border: { rect: Rect; width: number };
  date: StoryTextBox;
  /** Resident only: fixed "RESIDENT SHOWS" label. */
  header?: { box: Rect; text: string; fontSize: number; weight: number; align: 'center' };
  /** Fixed slot positions; the composer renders the first N chosen shows. */
  slots: StorySlot[];
  maxShows: number;
}

const box = (
  x: number,
  y: number,
  width: number,
  height: number,
  fontSize: number,
  weight = 700,
  align: 'left' | 'center' | 'right' = 'center',
): StoryTextBox => ({ box: { x, y, width, height }, fontSize, weight, align });

const guest: StoryTemplate = {
  id: 'ig-story',
  variant: 'guest',
  label: 'Instagram Story (guest)',
  audience: 'internal',
  canvas: { width: 1080, height: 1920 },
  fill: COLORS.navy,
  stroke: COLORS.orange,
  textColor: COLORS.orange,
  assets: {
    back: '/card-editor/story/back-guest.png',
    logo: '/card-editor/story/logo-guest.png',
    logoRect: { x: 93, y: 1562, width: 345, height: 258 },
  },
  border: { rect: { x: 55, y: 229, width: 972, height: 1472 }, width: 4 },
  date: box(86, 254, 648, 121, 85),
  slots: [
    {
      photoFrame: { x: 545, y: 501, width: 417, height: 327 },
      name: box(85, 501, 439, 64, 48),
      time: box(85, 577, 439, 62, 40, 700),
    },
    {
      photoFrame: { x: 545, y: 881, width: 417, height: 327 },
      name: box(93, 881, 435, 64, 48),
      time: box(93, 957, 435, 62, 40, 700),
    },
    {
      photoFrame: { x: 545, y: 1261, width: 417, height: 327 },
      name: box(93, 1264, 439, 64, 48),
      time: box(93, 1340, 439, 62, 40, 700),
    },
  ],
  maxShows: 3,
};

const resident: StoryTemplate = {
  id: 'ig-story-resident',
  variant: 'resident',
  label: 'Instagram Story (resident)',
  audience: 'internal',
  canvas: { width: 1080, height: 1920 },
  fill: COLORS.grey,
  stroke: COLORS.orange,
  textColor: COLORS.orange,
  assets: {
    back: '/card-editor/story/back-resident.png',
    logo: '/card-editor/story/logo-resident.png',
    logoRect: { x: 93, y: 1562, width: 345, height: 258 },
  },
  border: { rect: { x: 30, y: 229, width: 1014, height: 1472 }, width: 4 },
  date: box(85, 312, 648, 121, 85),
  header: { box: { x: 559, y: 194, width: 439, height: 64 }, text: 'RESIDENT SHOWS', fontSize: 48, weight: 700, align: 'center' },
  slots: [
    {
      photoFrame: { x: 419, y: 567, width: 596, height: 398 },
      name: box(85, 484, 652, 64, 48),
      artist: box(85, 567, 310, 55, 36, 400),
      time: box(85, 643, 320, 66, 40, 700),
      connectors: [
        { x: 138, y: 548, length: 19 },
        { x: 348, y: 548, length: 19 },
      ],
    },
    {
      photoFrame: { x: 419, y: 1145, width: 596, height: 398 },
      name: box(93, 1062, 652, 64, 48),
      artist: box(93, 1145, 310, 55, 36, 400),
      time: box(93, 1231, 312, 66, 40, 700),
      connectors: [
        { x: 146, y: 1126, length: 19 },
        { x: 356, y: 1126, length: 19 },
      ],
    },
  ],
  maxShows: 2,
};

export const STORY_TEMPLATES: StoryTemplate[] = [guest, resident];

export function getStoryTemplate(id: string): StoryTemplate | undefined {
  return STORY_TEMPLATES.find((t) => t.id === id);
}
